import * as React from 'react';
import cssVars from 'css-vars-ponyfill';
import Color from 'color';
import {CMSThemeMap, ColorSchemeType} from '../../models/cms/Theme';

interface AccentColors {
    customAccentColor?: string;
    customAccentHover?: string;
    customAccentHoverInverse?: string;
    customAccentColorText?: string;
}

interface PrimaryColors {
    customPrimaryColor?: string;
    customPrimaryBg?: string;
    customPrimaryBgTransparent?: string;
    customPrimaryHighlight?: string;
    customPrimaryText?: string;
    customPrimaryBodyText?: string;
}

type ThemeVariablesKey =
    | 'buttonPrimaryBg'
    | 'colorScheme'
    | 'customPrimaryBg'
    | 'uiPrimaryBg'
    | 'uiPrimaryText'
    | 'uiSecondaryBg'
    | 'uiTertiaryBg';

type BlackOrWhite = 'black' | 'white';

interface Props {
    theme: CMSThemeMap;
}

const Theme = (props: Props) => {
    React.useEffect(() => {
        cssVars();
    });

    const getColorValue = (key: ThemeVariablesKey) => {
        return props.theme.get(key) as ColorSchemeType;
    };

    const getContrastColor = (key: ThemeVariablesKey) => {
        const color = Color(getColorValue(key));
        const textColors = {white: '#f4f4f4', black: '#121212'};
        const textColor: BlackOrWhite = color.isLight() ? 'black' : 'white';
        const textColorValue = textColors[textColor];
        const contrastRatio = color.contrast(Color(textColorValue));
        const desiredContrastRatio = 3;
        if (contrastRatio < desiredContrastRatio) {
            console.warn(
                'Accessibility warning: WCAG 2 level A requires a contrast minimum ratio of at least 3: https://webaim.org/blog/wcag-2-0-and-link-colors',
                `${key}: ${color} has a ratio of ${contrastRatio.toFixed(
                    2
                )} with ${textColor} (${textColorValue}) text.`,
                `Please consider changing the ${key}.`,
                `Additional resource: https://webaim.org/resources/contrastchecker/?fcolor=${textColorValue.substring(
                    1
                )}&bcolor=${color.substring && color.substring(1)}`
            );
        }

        return textColorValue;
    };

    const setRelativeColor = (key: ThemeVariablesKey, s: number, l: number, a?: number) => {
        const color = Color(getColorValue(key));
        const colorHSL = color.hsl().object();

        return Color(getColorValue(key))
            .saturationl(colorHSL.s + s)
            .lightness(colorHSL.l + l)
            .alpha(a)
            .rgb();
    };

    const getAccentColors = (): AccentColors => {
        const customAccentColor = getColorValue('buttonPrimaryBg');

        if (!customAccentColor) return {};

        const customAccentColorText = getContrastColor('buttonPrimaryBg');
        const isCustomAccentColorDark: boolean = Color(customAccentColor).isDark();
        const setAccentColorLightness = (value: number) =>
            setRelativeColor('buttonPrimaryBg', 0, value, 1);
        const customAccentHover = isCustomAccentColorDark
            ? setAccentColorLightness(5)
            : setAccentColorLightness(-5);
        const customAccentHoverInverse = isCustomAccentColorDark
            ? setAccentColorLightness(-5)
            : setAccentColorLightness(5);

        return {
            customAccentColor,
            customAccentColorText,
            customAccentHover,
            customAccentHoverInverse
        };
    };

    const getPrimaryColors = (): PrimaryColors => {
        const getColorScheme: ColorSchemeType = getColorValue('colorScheme');
        const getCustomPrimaryBg = getColorValue('customPrimaryBg');

        if (!getCustomPrimaryBg && !getColorScheme) {
            return {
                customPrimaryBg: '',
                customPrimaryBgTransparent: '',
                customPrimaryColor: '',
                customPrimaryHighlight: 'var(--theme-ui-secondary-bg)',
                customPrimaryText: 'var(--theme-ui-primary-text)'
            };
        }
        const primaryColor = !getCustomPrimaryBg ? 'uiTertiaryBg' : 'customPrimaryBg';
        const customPrimaryColor = getColorValue(primaryColor);
        const setRelativeHSL = (s: number, l: number, a = 1) =>
            setRelativeColor(primaryColor, s, l, a);
        const customPrimaryBg = setRelativeHSL(-5, -5);
        const customPrimaryBgTransparent = setRelativeHSL(-5, -5, 0);
        const customPrimaryHighlight = setRelativeHSL(0, 5);
        const customPrimaryText = getContrastColor('customPrimaryBg');
        const customPrimaryBodyText = Color(customPrimaryText)
            .alpha(0.6)
            .rgb();

        return {
            customPrimaryBg,
            customPrimaryBgTransparent,
            customPrimaryColor,
            customPrimaryHighlight,
            customPrimaryText,
            customPrimaryBodyText
        };
    };

    const {
        customAccentColor,
        customAccentHover,
        customAccentHoverInverse,
        customAccentColorText
    }: AccentColors = getAccentColors();

    const {
        customPrimaryColor,
        customPrimaryBg,
        customPrimaryBgTransparent,
        customPrimaryHighlight,
        customPrimaryText,
        customPrimaryBodyText
    }: PrimaryColors = getPrimaryColors();

    return (
        <style>
            {`:root {
                --theme-ui-primary-bg: ${getColorValue('uiPrimaryBg')};
                --theme-ui-primary-text: ${getColorValue('uiPrimaryText')};
                --theme-ui-secondary-bg: ${getColorValue('uiSecondaryBg')};
                --theme-ui-tertiary-bg: ${getColorValue('uiTertiaryBg')};
                --theme-custom-color-primary: ${customPrimaryColor};
                --theme-custom-color-primary-bg: ${customPrimaryBg};
                --theme-custom-color-primary-bg-transparent: ${customPrimaryBgTransparent};
                --theme-custom-color-primary-highlight: ${customPrimaryHighlight};
                --theme-custom-color-primary-text: ${customPrimaryText};
                --theme-custom-color-primary-body-text: ${customPrimaryBodyText};
                --theme-custom-color-accent: ${customAccentColor};
                --theme-custom-color-accent-text: ${customAccentColorText};
                --theme-custom-color-accent-hover: ${customAccentHover};
                --theme-custom-color-accent-hover-inverse: ${customAccentHoverInverse};
            }`}
        </style>
    );
};

export default Theme;
